// Utility for Optimization Ingest Scheduler API.
// Every call accepts an optional Bearer token, so auth can be passed in at call site.
// Falls back to mock mode (in-memory) when AUTH_MODE != "ms_entra_id".

export type NRMetricsSchedules = {
    uuid?: string;
    entityGuid: string;
    entityName?: string;
    enableTime?: string;   // "HH:mm"
    disableTime?: string;  // "HH:mm"
    scheduleDays?: string[];
    ruleId?: string; // The associated Metric Normalization Rule.
    timezone?: string;  // IANA TZ
    active: boolean;
};

const BASE = String(process.env.REACT_APP_SPRING_BOOT_API_URL || "").replace(/\/+$/, "");

// -----------------------------------------------------------------------------
// API surface
// -----------------------------------------------------------------------------

export async function getMetricSchedules(entityGuid?: string, token?: string | null): Promise<NRMetricsSchedules[]> {

    const url = new URL(`${BASE}api/ingestscheduler/metricSchedules`);
    if (entityGuid) url.searchParams.set("entityGuid", entityGuid);
    const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`GET api/ingestscheduler/metricSchedules failed: ${res.status}`);
    return res.json();
}

export async function createMetricSchedule(
    payload: NRMetricsSchedules[],
    token?: string | null
): Promise<void> {

    const res = await fetch(`${BASE}api/ingestscheduler/metricSchedules`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).message ?? `POST api/ingestscheduler/metricSchedules failed: ${res.status}`);
}

export async function deleteMetricScheduleDay(
    uuid: string,
    day: string,
    token?: string | null
): Promise<void> {

    const url = new URL(`${BASE}api/ingestscheduler/metricSchedules`);
    url.searchParams.set("uuid", uuid);
    url.searchParams.set("day", day);

    const res = await fetch(url.toString(), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`DELETE api/ingestscheduler/metricSchedules failed: ${res.status}`);
}

export async function getOrphanedSchedules(token?: string | null): Promise<NRMetricsSchedules[]> {
    const res = await fetch(`${BASE}/metricSchedules/orphans`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`GET api/ingestscheduler/metricSchedules/orphans failed: ${res.status}`);
    return res.json();
}

export const optimizationIngestSchedulerApi = {
    getMetricSchedules,
    createMetricSchedule,
    deleteMetricScheduleDay,
    getOrphanedSchedules
};
